import { useState } from "react";
import {
  XMarkIcon,
  CalendarIcon,
  MapPinIcon,
  PhotoIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import { supabase, TABLES } from "../supabase";

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventCreated: () => void;
}

export default function CreateEventModal({
  isOpen,
  onClose,
  onEventCreated,
}: CreateEventModalProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    city: "",
    district: "",
    maxAttendees: "",
    category: "community",
    image: null as File | null,
  });

  const districts = [
    "Ampara",
    "Anuradhapura",
    "Badulla",
    "Batticaloa",
    "Colombo",
    "Galle",
    "Gampaha",
    "Hambantota",
    "Jaffna",
    "Kalutara",
    "Kandy",
    "Kegalle",
    "Kilinochchi",
    "Kurunegala",
    "Mannar",
    "Matale",
    "Matara",
    "Monaragala",
    "Mullaitivu",
    "Nuwara Eliya",
    "Polonnaruwa",
    "Puttalam",
    "Ratnapura",
    "Trincomalee",
    "Vavuniya",
  ];

  const categories = [
    { id: "community", name: "Community Drive", icon: "🤝" },
    { id: "workshop", name: "Workshop/Training", icon: "📚" },
    { id: "appreciation", name: "Appreciation Event", icon: "🎉" },
    { id: "fundraising", name: "Fundraising", icon: "💰" },
    { id: "awareness", name: "Awareness Campaign", icon: "📢" },
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({ ...prev, image: e.target.files![0] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (!user) {
      setError("Please sign in to create events.");
      setLoading(false);
      return;
    }

    // Validation
    if (
      !formData.title ||
      !formData.description ||
      !formData.date ||
      !formData.time
    ) {
      setError("Please fill in all required fields.");
      setLoading(false);
      return;
    }

    try {
      let imageUrl = "";

      // Check if event-images bucket exists before uploading
      if (formData.image) {
        try {
          await supabase.storage.from("event-images").list("", { limit: 1 });
        } catch (bucketError: any) {
          console.error("Event bucket access error:", bucketError);
          throw new Error(
            `Cannot access storage bucket 'event-images'. Please ensure the bucket exists and is public. Error: ${bucketError.message}`
          );
        }
      }

      // Upload image if provided
      if (formData.image) {
        const fileExt = formData.image.name.split(".").pop();
        const fileName = `${Date.now()}_${Math.random()
          .toString(36)
          .substring(2)}.${fileExt}`;
        const filePath = `eventImages/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from("event-images")
          .upload(filePath, formData.image);

        if (uploadError) {
          console.error("Event upload error:", uploadError);

          // If it's a bucket not found error, provide specific guidance
          if (
            uploadError.message.includes("not found") ||
            uploadError.message.includes("does not exist")
          ) {
            throw new Error(
              "Storage bucket 'event-images' not found. Please ensure the bucket is created in your Supabase project Storage section and is set to public."
            );
          }

          throw new Error(`Failed to upload image: ${uploadError.message}`);
        }

        const {
          data: { publicUrl },
        } = supabase.storage.from("event-images").getPublicUrl(filePath);

        imageUrl = publicUrl;
      }

      const eventDateTime = new Date(`${formData.date}T${formData.time}`);

      // Create event document
      const { error: eventError } = await supabase.from(TABLES.EVENTS).insert({
        title: formData.title,
        description: formData.description,
        event_date: eventDateTime.toISOString(),
        location: {
          address: formData.location,
          city: formData.city,
          district: formData.district,
        },
        max_attendees: parseInt(formData.maxAttendees) || 100,
        category: formData.category,
        image_url: imageUrl,
        organizer_id: user.id,
        attendee_ids: [],
        current_attendees: 0,
        status: "upcoming",
        contact_info: {
          name: user.user_metadata?.name || user.email,
          email: user.email,
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });

      if (eventError) {
        console.error("Event creation error:", eventError);
        throw eventError;
      }

      setSuccess("Event created successfully!");

      // Create notification for the event creator
      const { error: notificationError } = await supabase
        .from(TABLES.NOTIFICATIONS)
        .insert({
          user_id: user.id,
          type: "event_created",
          title: "Event Created Successfully!",
          message: `Your event "${formData.title}" has been created and is now visible to the community.`,
          is_read: false,
          created_at: new Date().toISOString(),
        });

      if (notificationError) {
        console.error("Failed to create notification:", notificationError);
      }

      onEventCreated();
      setTimeout(() => {
        onClose();
        // Reset form
        setFormData({
          title: "",
          description: "",
          date: "",
          time: "",
          location: "",
          city: "",
          district: "",
          maxAttendees: "",
          category: "community",
          image: null,
        });
      }, 1500);
    } catch (err: any) {
      setError("Failed to create event. Please try again.");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-3xl bg-white/95 backdrop-blur-2xl rounded-3xl shadow-3xl max-h-[90vh] overflow-y-auto border border-white/30">
        <div className="flex items-center justify-between p-8 border-b border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900">
            Create New Event
          </h3>
          <button
            onClick={onClose}
            className="p-3 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-2xl transition-all duration-300 transform hover:scale-110"
            title="Close modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {/* Event Title */}
          <div>
            <label className="block mb-3 text-sm font-bold text-slate-700">
              Event Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="e.g., Community Food Drive"
              className="input-field"
              required
            />
          </div>

          {/* Event Description */}
          <div>
            <label className="block mb-3 text-sm font-bold text-slate-700">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Describe your event, its purpose, and what attendees can expect..."
              rows={4}
              className="input-field"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block mb-4 text-sm font-bold text-slate-700">
              Event Category *
            </label>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {categories.map((category) => (
                <label
                  key={category.id}
                  className="flex items-center p-4 border-2 border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50 hover:border-emerald-200 transition-all duration-300 transform hover:scale-105"
                >
                  <input
                    type="radio"
                    value={category.id}
                    checked={formData.category === category.id}
                    onChange={(e) =>
                      handleInputChange("category", e.target.value)
                    }
                    className="mr-4 w-5 h-5 text-emerald-600"
                  />
                  <span className="mr-3 text-xl">{category.icon}</span>
                  <span className="text-sm font-semibold">{category.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="block mb-3 text-sm font-bold text-slate-700">
                <CalendarIcon className="inline w-5 h-5 mr-2" />
                Event Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange("date", e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="input-field"
                title="Select event date"
                required
              />
            </div>
            <div>
              <label className="block mb-3 text-sm font-bold text-slate-700">
                Event Time *
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange("time", e.target.value)}
                className="input-field"
                title="Select event time"
                required
              />
            </div>
          </div>

          {/* Location Details */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="block mb-3 text-sm font-bold text-slate-700">
                <MapPinIcon className="inline w-5 h-5 mr-2" />
                Venue/Location *
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                placeholder="e.g., Community Center, Park"
                className="input-field"
                title="Enter venue or location"
                required
              />
            </div>
            <div>
              <label className="block mb-3 text-sm font-bold text-slate-700">
                City *
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                placeholder="e.g., Colombo"
                className="input-field"
                title="Enter city name"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="block mb-3 text-sm font-bold text-slate-700">
                District *
              </label>
              <select
                value={formData.district}
                onChange={(e) => handleInputChange("district", e.target.value)}
                className="input-field"
                required
                title="Select a district"
              >
                <option value="">Select District</option>
                {districts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-3 text-sm font-bold text-slate-700">
                <UsersIcon className="inline w-5 h-5 mr-2" />
                Max Attendees
              </label>
              <input
                type="number"
                value={formData.maxAttendees}
                onChange={(e) =>
                  handleInputChange("maxAttendees", e.target.value)
                }
                placeholder="e.g., 50"
                min="1"
                className="input-field"
                title="Maximum number of attendees"
              />
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block mb-3 text-sm font-bold text-slate-700">
              <PhotoIcon className="inline w-5 h-5 mr-2" />
              Event Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="input-field"
              title="Upload an event image"
            />
            <p className="mt-2 text-sm text-slate-500 font-semibold">
              Upload an image for your event (optional)
            </p>
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="p-6 text-red-700 bg-red-100 border-2 border-red-200 rounded-2xl shadow-lg">
              <div className="font-bold">{error}</div>
            </div>
          )}

          {success && (
            <div className="p-6 text-emerald-700 bg-emerald-100 border-2 border-emerald-200 rounded-2xl shadow-lg">
              <div className="font-bold">{success}</div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 text-sm font-bold text-slate-700 bg-white border-2 border-slate-300 rounded-2xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-300 transform hover:scale-105"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-4 text-sm font-bold text-white border border-transparent rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 disabled:opacity-50 transition-all duration-500 transform hover:scale-105 shadow-xl hover:shadow-2xl"
            >
              {loading ? "Creating..." : "Create Event"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
